<template>
  <div class="animated fadeIn device-health">

    <!-- Summary Strip -->
    <b-row class="mb-4">
      <b-col sm="6" lg="3">
        <b-card class="summary-card text-center" body-class="p-3">
          <div class="summary-count text-success">{{ counts.healthy }}</div>
          <div class="summary-label"><i class="fa fa-check-circle mr-1"></i>Healthy</div>
        </b-card>
      </b-col>
      <b-col sm="6" lg="3">
        <b-card class="summary-card text-center" body-class="p-3">
          <div class="summary-count text-warning">{{ counts.warning }}</div>
          <div class="summary-label"><i class="fa fa-exclamation-circle mr-1"></i>Warning</div>
        </b-card>
      </b-col>
      <b-col sm="6" lg="3">
        <b-card class="summary-card text-center" body-class="p-3">
          <div class="summary-count text-danger">{{ counts.critical }}</div>
          <div class="summary-label"><i class="fa fa-times-circle mr-1"></i>Critical</div>
        </b-card>
      </b-col>
      <b-col sm="6" lg="3">
        <b-card class="summary-card text-center" body-class="p-3">
          <div class="summary-count text-secondary">{{ counts.offline }}</div>
          <div class="summary-label"><i class="fa fa-power-off mr-1"></i>Offline</div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Main Table -->
    <b-card>
      <div slot="header" class="d-flex align-items-center justify-content-between">
        <span><i class="fa fa-heartbeat mr-2"></i>Device Health</span>
        <div class="d-flex align-items-center">
          <small v-if="lastRefresh" class="text-muted mr-3">
            Updated {{ lastRefresh }}
          </small>
          <b-button variant="outline-primary" size="sm" :disabled="loadingMetrics" @click="refresh">
            <i class="fa fa-refresh" :class="{ 'fa-spin': loadingMetrics }"></i> Refresh
          </b-button>
        </div>
      </div>

      <div v-if="$apollo.loading && rows.length === 0" class="text-center py-5">
        <b-spinner></b-spinner>
        <p class="mt-2 text-muted">Loading devices...</p>
      </div>

      <template v-else>
        <!-- Filters -->
        <b-row class="mb-3">
          <b-col md="5">
            <b-form-input
              v-model="search"
              placeholder="Search by name, serial or type..."
              debounce="250"
            ></b-form-input>
          </b-col>
          <b-col md="4">
            <b-form-select v-model="filterStatus" :options="statusFilterOptions"></b-form-select>
          </b-col>
          <b-col md="3">
            <b-form-select v-model="filterType" :options="typeFilterOptions"></b-form-select>
          </b-col>
        </b-row>

        <b-table
          :items="filteredRows"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          striped
          hover
          responsive
          show-empty
          empty-text="No devices match the current filter"
        >
          <!-- Device name — links to its configuration (admins only; the route is
               admin-guarded, so a non-admin would just be bounced to the dashboard) -->
          <template #cell(name)="{ item }">
            <router-link
              v-if="isAdmin"
              :to="{ name: 'Device Configuration', query: { device: item.serial } }"
              title="Open device configuration"
            >{{ item.name }}</router-link>
            <span v-else>{{ item.name }}</span>
          </template>

          <!-- Status badge -->
          <template #cell(status)="{ item }">
            <b-badge :variant="item.statusVariant" class="status-badge">
              <i :class="item.statusIcon" class="mr-1"></i>
              {{ item.statusLabel }}
            </b-badge>
          </template>

          <!-- Last seen -->
          <template #cell(lastSeen)="{ item }">
            <span v-if="item.loading" class="text-muted">
              <b-spinner small></b-spinner>
            </span>
            <span v-else-if="item.lastSeenMs === null" class="text-muted fst-italic">No data</span>
            <span v-else :class="item.lastSeenClass">
              {{ item.lastSeenAgo }}
              <br>
              <small class="text-muted">{{ item.lastSeenTimestamp }}</small>
            </span>
          </template>

          <!-- Expected interval -->
          <template #cell(publishInt)="{ item }">
            <span v-if="item.publishInt">{{ formatInterval(item.publishInt) }}</span>
            <span v-else class="text-muted">—</span>
          </template>

          <!-- Site -->
          <template #cell(site)="{ item }">
            {{ item.site || '—' }}
          </template>

          <!-- Charging status -->
          <template #cell(charging)="{ item }">
            <span v-if="item.chargingLoading" class="text-muted">
              <b-spinner small></b-spinner>
            </span>
            <b-badge
              v-else
              :variant="item.chargingVariant"
              class="status-badge"
              :id="`chg-${item.id}`"
            >
              <i :class="item.chargingIcon" class="mr-1"></i>
              {{ item.chargingLabel }}
            </b-badge>
            <b-tooltip
              v-if="item.inV !== null && !item.chargingLoading"
              :target="`chg-${item.id}`"
              placement="top"
            >
              <span v-if="item.inV === 0">No input voltage detected</span>
              <span v-else>
                Input: {{ item.inV.toFixed(2) }}V
                <span v-if="item.inV >= 2"> — charging well</span>
                <span v-else> — charging slowly</span>
              </span>
            </b-tooltip>
          </template>

          <!-- Battery -->
          <template #cell(battery)="{ item }">
            <span v-if="item.batteryLoading" class="text-muted">
              <b-spinner small></b-spinner>
            </span>
            <b-badge
              v-else
              :variant="item.batteryVariant"
              class="status-badge"
              :id="`bat-${item.id}`"
            >
              <i :class="item.batteryIcon" class="mr-1"></i>
              {{ item.batteryLabel }}
            </b-badge>
            <b-tooltip
              v-if="item.batChg !== null && !item.batteryLoading"
              :target="`bat-${item.id}`"
              placement="top"
            >
              Battery {{ item.batChg.toFixed(1) }}%
              <span v-if="item.batteryKey === 'critical'"> — critical, check charging</span>
              <span v-else-if="item.batteryKey === 'low'"> — low</span>
            </b-tooltip>
          </template>

          <!-- Signal bars -->
          <template #cell(signal)="{ item }">
            <div
              class="signal-bars"
              :title="signalData[item.id] != null ? 'Signal: ' + signalData[item.id] : 'No signal data'"
            >
              <template v-if="signalData[item.id] != null">
                <span
                  v-for="bar in 4"
                  :key="bar"
                  class="signal-bar"
                  :class="signalBarClass(item.id, bar)"
                ></span>
              </template>
              <span v-else class="text-muted" style="font-size:0.7rem;">—</span>
            </div>
          </template>

          <!-- Enabled toggle -->
          <template #cell(enabled)="{ item }">
            <b-badge :variant="item.enabled ? 'success' : 'secondary'">
              {{ item.enabled ? 'Active' : 'Disabled' }}
            </b-badge>
          </template>
        </b-table>
      </template>
    </b-card>

  </div>
</template>

<script>
import moment from 'moment-timezone'
import { DEVICES_HEALTH_QUERY, METRICS_QUERY } from '@/constants/graphql'
import { getUser } from '@/lib/auth'

// Primary metric to query per device type to detect last activity
const TYPE_METRIC = {
  well: 'depth_f',
  flume: 'flow_cfs',
  bubbler: 'pres_hpa',
  river: 'depth_f',
  rain: 'rain_in',
  plunge: 'tmp_f'
}
// How far back to look for the last reading (30 days)
const LOOKBACK = '30d-ago'

export default {
  name: 'DeviceHealth',
  data () {
    return {
      loadingMetrics: false,
      lastRefresh: null,
      metricData: {},    // keyed by device.id → { lastSeenMs: Number|null }
      chargingData: {},  // keyed by device.id → { inV: Number|null }
      batteryData: {},   // keyed by device.id → { batChg: Number|null }
      signalData: {},    // keyed by device.id → Number|null
      search: '',
      filterStatus: 'all',
      filterType: 'all',
      sortBy: 'statusOrder',
      sortDesc: false,
      fields: [
        { key: 'status',     label: 'Health',    sortable: true,  sortKey: 'statusOrder' },
        { key: 'name',       label: 'Device',    sortable: true },
        { key: 'type',       label: 'Type',      sortable: true },
        { key: 'site',       label: 'Site',      sortable: true },
        { key: 'lastSeen',   label: 'Last Seen', sortable: true,  sortKey: 'lastSeenMs' },
        { key: 'publishInt', label: 'Report Interval', sortable: false },
        { key: 'charging',   label: 'Charging',  sortable: true,  sortKey: 'chargingOrder' },
        { key: 'battery',    label: 'Battery',   sortable: true,  sortKey: 'batteryOrder' },
        { key: 'signal',     label: 'Signal',    sortable: false, thClass: 'd-none d-md-table-cell', tdClass: 'd-none d-md-table-cell' },
        { key: 'enabled',    label: 'Status',    sortable: true }
      ],
      statusFilterOptions: [
        { value: 'all',      text: 'All statuses' },
        { value: 'healthy',  text: 'Healthy' },
        { value: 'warning',  text: 'Warning' },
        { value: 'critical', text: 'Critical' },
        { value: 'offline',  text: 'Offline' },
        { value: 'unknown',  text: 'No data' }
      ]
    }
  },
  apollo: {
    devices: {
      query: DEVICES_HEALTH_QUERY,
      fetchPolicy: 'cache-and-network',
      result () {
        this.fetchAllMetrics()
      }
    }
  },
  computed: {
    typeFilterOptions () {
      const types = [...new Set((this.devices || []).map(d => d.type).filter(Boolean))].sort()
      return [
        { value: 'all', text: 'All types' },
        ...types.map(t => ({ value: t, text: t.charAt(0).toUpperCase() + t.slice(1) }))
      ]
    },
    isAdmin () {
      var user = getUser()
      return !!(user && user.role === 'ADMIN')
    },
    rows () {
      if (!this.devices) return []
      return this.devices.map(device => {
        const meta = this.metricData[device.id]
        const charging = this.chargingData[device.id]
        const battery = this.batteryData[device.id]
        const loading = !meta
        const lastSeenMs = meta ? meta.lastSeenMs : null
        const publishInt = device.config ? device.config.publishInt : null
        const inV = charging ? charging.inV : null
        const batChg = battery ? battery.batChg : null

        const status = this.computeStatus(device, lastSeenMs, publishInt)
        const chargingStatus = this.computeChargingStatus(inV, charging)
        const batteryStatus = this.computeBatteryStatus(batChg, battery)

        return {
          ...device,
          site: device.site ? device.site.name : null,
          publishInt,
          loading,
          lastSeenMs,
          lastSeenAgo: lastSeenMs ? moment(lastSeenMs).fromNow() : null,
          lastSeenTimestamp: lastSeenMs ? moment(lastSeenMs).format('YYYY-MM-DD HH:mm') : null,
          lastSeenClass: this.lastSeenClass(lastSeenMs, publishInt),
          inV,
          chargingLoading: !charging,
          batChg,
          batteryLoading: !battery,
          ...status,
          ...chargingStatus,
          ...batteryStatus
        }
      })
    },
    filteredRows () {
      return this.rows.filter(row => {
        if (this.filterStatus !== 'all' && row.statusKey !== this.filterStatus) return false
        if (this.filterType !== 'all' && row.type !== this.filterType) return false
        if (this.search) {
          const s = this.search.toLowerCase()
          if (
            !row.name.toLowerCase().includes(s) &&
            !row.serial.toLowerCase().includes(s) &&
            !row.type.toLowerCase().includes(s)
          ) return false
        }
        return true
      })
    },
    counts () {
      const c = { healthy: 0, warning: 0, critical: 0, offline: 0 }
      this.rows.forEach(r => {
        if (r.statusKey === 'healthy') c.healthy++
        else if (r.statusKey === 'warning') c.warning++
        else if (r.statusKey === 'critical') c.critical++
        else if (r.statusKey === 'offline') c.offline++
      })
      return c
    }
  },
  methods: {
    computeStatus (device, lastSeenMs, publishInt) {
      if (!device.enabled) {
        return { statusKey: 'offline', statusLabel: 'Offline', statusVariant: 'secondary', statusIcon: 'fa fa-power-off', statusOrder: 4 }
      }
      if (lastSeenMs === null) {
        return { statusKey: 'unknown', statusLabel: 'No Data', statusVariant: 'light', statusIcon: 'fa fa-question-circle', statusOrder: 5 }
      }

      const ageMs = Date.now() - lastSeenMs
      const thresholdMs = publishInt ? publishInt * 2 * 1000 : 2 * 60 * 60 * 1000  // 2× publishInt or 2h default
      const criticalMs = 24 * 60 * 60 * 1000  // 24h

      if (ageMs > criticalMs) {
        return { statusKey: 'critical', statusLabel: 'Critical', statusVariant: 'danger', statusIcon: 'fa fa-times-circle', statusOrder: 1 }
      }
      if (ageMs > thresholdMs) {
        return { statusKey: 'warning', statusLabel: 'Warning', statusVariant: 'warning', statusIcon: 'fa fa-exclamation-circle', statusOrder: 2 }
      }
      return { statusKey: 'healthy', statusLabel: 'Healthy', statusVariant: 'success', statusIcon: 'fa fa-check-circle', statusOrder: 3 }
    },
    lastSeenClass (lastSeenMs, publishInt) {
      if (!lastSeenMs) return 'text-muted'
      const ageMs = Date.now() - lastSeenMs
      const threshold = publishInt ? publishInt * 2 * 1000 : 7200000
      if (ageMs > 86400000) return 'text-danger'
      if (ageMs > threshold) return 'text-warning'
      return 'text-success'
    },
    formatInterval (s) {
      if (!s) return '—'
      if (s < 60) return `${s}s`
      if (s < 3600) return `${Math.round(s / 60)}m`
      return `${Math.round(s / 3600)}h`
    },
    computeChargingStatus (inV, chargingEntry) {
      if (!chargingEntry) {
        return { chargingKey: 'loading', chargingLabel: '...', chargingVariant: 'secondary', chargingIcon: 'fa fa-circle-o-notch fa-spin', chargingOrder: 0 }
      }
      if (inV === null) {
        return { chargingKey: 'unknown', chargingLabel: 'No Data', chargingVariant: 'light', chargingIcon: 'fa fa-question-circle', chargingOrder: 0 }
      }
      if (inV === 0) {
        return { chargingKey: 'none', chargingLabel: 'Not Charging', chargingVariant: 'secondary', chargingIcon: 'fa fa-plug', chargingOrder: 1 }
      }
      if (inV < 2) {
        return { chargingKey: 'slow', chargingLabel: `Slow (${inV.toFixed(1)}V)`, chargingVariant: 'warning', chargingIcon: 'fa fa-bolt', chargingOrder: 2 }
      }
      return { chargingKey: 'charging', chargingLabel: `Charging (${inV.toFixed(1)}V)`, chargingVariant: 'success', chargingIcon: 'fa fa-bolt', chargingOrder: 3 }
    },
    computeBatteryStatus (batChg, batteryEntry) {
      if (!batteryEntry) {
        return { batteryKey: 'loading', batteryLabel: '...', batteryVariant: 'secondary', batteryIcon: 'fa fa-circle-o-notch fa-spin', batteryOrder: 999 }
      }
      if (batChg === null) {
        return { batteryKey: 'unknown', batteryLabel: 'No Data', batteryVariant: 'light', batteryIcon: 'fa fa-question-circle', batteryOrder: 998 }
      }
      const pct = Math.max(0, Math.min(100, batChg))
      const label = `${Math.round(pct)}%`
      // Icon tiers match FontAwesome battery levels; variants: green >=50, yellow 20-50, red <20
      if (pct >= 87.5) return { batteryKey: 'full',    batteryLabel: label, batteryVariant: 'success', batteryIcon: 'fa fa-battery-full',           batteryOrder: pct }
      if (pct >= 62.5) return { batteryKey: 'high',    batteryLabel: label, batteryVariant: 'success', batteryIcon: 'fa fa-battery-three-quarters', batteryOrder: pct }
      if (pct >= 37.5) return { batteryKey: 'mid',     batteryLabel: label, batteryVariant: 'warning', batteryIcon: 'fa fa-battery-half',           batteryOrder: pct }
      if (pct >= 15)   return { batteryKey: 'low',     batteryLabel: label, batteryVariant: 'warning', batteryIcon: 'fa fa-battery-quarter',        batteryOrder: pct }
      return               { batteryKey: 'critical',batteryLabel: label, batteryVariant: 'danger',  batteryIcon: 'fa fa-battery-empty',          batteryOrder: pct }
    },
    async fetchAllMetrics () {
      if (!this.devices || this.devices.length === 0) return
      this.loadingMetrics = true

      // Mark all as loading
      this.devices.forEach(d => {
        if (!this.metricData[d.id]) this.$set(this.metricData, d.id, null)
        if (!this.chargingData[d.id]) this.$set(this.chargingData, d.id, null)
        if (!this.batteryData[d.id]) this.$set(this.batteryData, d.id, null)
      })

      await Promise.all([
        ...this.devices.map(device => this.fetchLastSeen(device)),
        ...this.devices.map(device => this.fetchInV(device)),
        ...this.devices.map(device => this.fetchBattery(device)),
        ...this.devices.map(device => this.fetchSignal(device))
      ])

      this.loadingMetrics = false
      this.lastRefresh = moment().format('HH:mm:ss')
    },
    async fetchLastSeen (device) {
      if (!TYPE_METRIC[device.type]) {
        this.$set(this.metricData, device.id, { lastSeenMs: null })
        return
      }
      const metric = TYPE_METRIC[device.type]
      try {
        const result = await this.$apollo.query({
          query: METRICS_QUERY,
          variables: {
            where: {
              device: device.serial,
              type: device.type,
              metric,
              start: LOOKBACK,
              limit: 1000
            }
          },
          fetchPolicy: 'network-only'
        })

        const data = result.data.metrics && result.data.metrics.data
        if (!data || Object.keys(data).length === 0) {
          this.$set(this.metricData, device.id, { lastSeenMs: null })
          return
        }

        const deviceData = data[Object.keys(data)[0]]
        if (!deviceData || !deviceData.values || !deviceData.values.length) {
          this.$set(this.metricData, device.id, { lastSeenMs: null })
          return
        }

        const columns = deviceData.columns
        const timeIndex = columns.indexOf('time') !== -1 ? columns.indexOf('time') : 2
        const times = deviceData.values
          .map(row => row[timeIndex])
          .filter(t => t != null)

        const lastSeenMs = times.length > 0 ? Math.max(...times) * 1000 : null
        this.$set(this.metricData, device.id, { lastSeenMs })
      } catch (e) {
        this.$set(this.metricData, device.id, { lastSeenMs: null })
      }
    },
    async fetchBattery (device) {
      if (!TYPE_METRIC[device.type]) {
        this.$set(this.batteryData, device.id, { batChg: null })
        return
      }
      try {
        const result = await this.$apollo.query({
          query: METRICS_QUERY,
          variables: {
            where: {
              device: device.serial,
              type: device.type,
              metric: 'bat_chg',
              start: LOOKBACK,
              limit: 1000
            }
          },
          fetchPolicy: 'network-only'
        })

        const data = result.data.metrics && result.data.metrics.data
        if (!data || Object.keys(data).length === 0) {
          this.$set(this.batteryData, device.id, { batChg: null })
          return
        }

        const deviceData = data[Object.keys(data)[0]]
        if (!deviceData || !deviceData.values || !deviceData.values.length) {
          this.$set(this.batteryData, device.id, { batChg: null })
          return
        }

        const columns = deviceData.columns
        const timeIndex = columns.indexOf('time') !== -1 ? columns.indexOf('time') : 2
        const valueIndex = columns.indexOf('bat_chg')

        if (valueIndex === -1) {
          this.$set(this.batteryData, device.id, { batChg: null })
          return
        }

        let latestTime = -Infinity
        let latestBatChg = null
        deviceData.values.forEach(row => {
          const t = row[timeIndex]
          const v = row[valueIndex]
          if (t != null && t > latestTime && v != null) {
            latestTime = t
            latestBatChg = v
          }
        })

        this.$set(this.batteryData, device.id, { batChg: latestBatChg })
      } catch (e) {
        this.$set(this.batteryData, device.id, { batChg: null })
      }
    },
    async fetchInV (device) {
      if (!TYPE_METRIC[device.type]) {
        this.$set(this.chargingData, device.id, { inV: null })
        return
      }
      try {
        const result = await this.$apollo.query({
          query: METRICS_QUERY,
          variables: {
            where: {
              device: device.serial,
              type: device.type,
              metric: 'in_v',
              start: LOOKBACK,
              limit: 1000
            }
          },
          fetchPolicy: 'network-only'
        })

        const data = result.data.metrics && result.data.metrics.data
        if (!data || Object.keys(data).length === 0) {
          this.$set(this.chargingData, device.id, { inV: null })
          return
        }

        const deviceData = data[Object.keys(data)[0]]
        if (!deviceData || !deviceData.values || !deviceData.values.length) {
          this.$set(this.chargingData, device.id, { inV: null })
          return
        }

        const columns = deviceData.columns
        const timeIndex = columns.indexOf('time') !== -1 ? columns.indexOf('time') : 2
        const valueIndex = columns.indexOf('in_v')

        if (valueIndex === -1) {
          this.$set(this.chargingData, device.id, { inV: null })
          return
        }

        let latestTime = -Infinity
        let latestInV = null
        deviceData.values.forEach(row => {
          const t = row[timeIndex]
          const v = row[valueIndex]
          if (t != null && t > latestTime && v != null) {
            latestTime = t
            latestInV = v
          }
        })

        this.$set(this.chargingData, device.id, { inV: latestInV })
      } catch (e) {
        this.$set(this.chargingData, device.id, { inV: null })
      }
    },
    signalLevel (deviceId) {
      const sig = this.signalData[deviceId]
      if (sig == null) return null
      return sig >= 60 ? 4 : sig >= 40 ? 3 : sig >= 20 ? 2 : 1
    },
    signalBarClass (deviceId, bar) {
      const level = this.signalLevel(deviceId)
      if (level === null || bar > level) return 'signal-bar--empty'
      return level >= 3 ? 'signal-bar--good' : level === 2 ? 'signal-bar--ok' : 'signal-bar--poor'
    },
    async fetchSignal (device) {
      if (!TYPE_METRIC[device.type]) {
        this.$set(this.signalData, device.id, null)
        return
      }
      try {
        const result = await this.$apollo.query({
          query: METRICS_QUERY,
          variables: {
            where: {
              device: device.serial,
              type: device.type,
              metric: 'cell_sig',
              start: LOOKBACK,
              limit: 1000
            }
          },
          fetchPolicy: 'network-only'
        })
        const data = result.data.metrics && result.data.metrics.data
        if (!data || !Object.keys(data).length) { this.$set(this.signalData, device.id, null); return }
        const deviceData = data[Object.keys(data)[0]]
        if (!deviceData || !deviceData.values || !deviceData.values.length) { this.$set(this.signalData, device.id, null); return }
        const columns = deviceData.columns
        const sigIndex = columns.indexOf('cell_sig')
        const timeIndex = columns.indexOf('time') !== -1 ? columns.indexOf('time') : 2
        if (sigIndex === -1) { this.$set(this.signalData, device.id, null); return }
        let latestTime = -Infinity
        let latestSig = null
        deviceData.values.forEach(row => {
          if (row[timeIndex] != null && row[timeIndex] > latestTime && row[sigIndex] != null) {
            latestTime = row[timeIndex]
            latestSig = row[sigIndex]
          }
        })
        this.$set(this.signalData, device.id, latestSig)
      } catch (e) {
        this.$set(this.signalData, device.id, null)
      }
    },
    refresh () {
      this.metricData = {}
      this.chargingData = {}
      this.batteryData = {}
      this.signalData = {}
      this.$apollo.queries.devices.refetch().then(() => {
        this.fetchAllMetrics()
      })
    }
  }
}
</script>

<style scoped>
.device-health {
  padding: 20px;
}

.summary-card {
  border-top: 3px solid #dee2e6;
}

.summary-count {
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1;
}

.summary-label {
  font-size: 0.85rem;
  color: #6c757d;
  margin-top: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.status-badge {
  font-size: 0.8rem;
  padding: 0.35em 0.6em;
}

.signal-bars {
  display: inline-flex;
  align-items: flex-end;
  gap: 2px;
  height: 16px;
}
.signal-bar {
  width: 5px;
  border-radius: 1px;
}
.signal-bar:nth-child(1) { height: 4px; }
.signal-bar:nth-child(2) { height: 7px; }
.signal-bar:nth-child(3) { height: 11px; }
.signal-bar:nth-child(4) { height: 15px; }
.signal-bar--good  { background-color: #28a745; }
.signal-bar--ok    { background-color: #ffc107; }
.signal-bar--poor  { background-color: #dc3545; }
.signal-bar--empty { background-color: #dee2e6; }
</style>
